import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";
import heroAsset from "@/assets/blog/pcos-diet-plan-hero.webp";
import plateAsset from "@/assets/blog/pcos-diet-plan-plate.webp";
import weekAsset from "@/assets/blog/pcos-diet-plan-week.webp";
import faqAsset from "@/assets/blog/pcos-diet-plan-faq.webp";

const SLUG = "7-day-pcos-diet-plan-india";
const URL = `https://truwe.in/blog/${SLUG}`;
const TITLE = "7 Day PCOS Diet Plan (India): Meals & Chart | TruWe";
const PUBLISHED = "2026-09-18";

const FAQS = [
  {
    q: "Is this 7-day PCOS diet plan suitable for PCOD as well?",
    a: "Yes. PCOS and PCOD are used interchangeably in India, and the same insulin-focused, low-GI eating pattern applies to both, regardless of which term your report uses.",
  },
  {
    q: "Can I follow this PCOS diet plan if I'm a non-vegetarian?",
    a: "Yes — swap the vegetarian protein in any lunch or dinner (paneer, dal, tofu) for egg, chicken, or fish in a similar portion, as shown in the meal table above. The rest of the plate structure stays the same.",
  },
  {
    q: "Do I need to cut rice completely on a PCOS diet?",
    a: "No. Keep white rice to around half a cup, always paired with dal or vegetables rather than eaten alone, and prefer brown rice or millets like ragi and jowar when you can.",
  },
  {
    q: "Can I eat fruits on a PCOS diet plan?",
    a: "Yes, whole fruits with their skin — apple, guava, orange, pomegranate — are encouraged, since the fibre slows sugar absorption. Fruit juices are the exception to limit, as juicing removes the fibre and spikes blood sugar faster.",
  },
  {
    q: "Is intermittent fasting good for PCOS?",
    a: "It can help some women by improving insulin sensitivity, but it isn't necessary for this plan to work, and long fasting windows can backfire by increasing cortisol and cravings in others. It's worth trying only under guidance from your doctor or dietitian, not as a default addition.",
  },
  {
    q: "How long before I see results from a 7-day PCOS diet plan?",
    a: "Most women following a consistent low-GI, protein-forward pattern notice changes in energy and cravings within a few weeks, with cycle regularity typically improving around 8–12 weeks. Results depend on consistency more than any single perfect day.",
  },
  {
    q: "Does dairy make PCOS worse?",
    a: "Current evidence doesn't support cutting dairy for PCOS in general — curd and buttermilk fit well into a low-GI Indian plate and add useful protein. If a specific dairy food personally triggers skin or digestive issues for you, it's fine to reduce that one food rather than dairy as a whole category.",
  },
  {
    q: "Can I repeat this 7-day PCOS diet plan every week?",
    a: "Yes — it's designed as a rotating weekly pattern, not a one-time challenge. Swap individual meals for variety while keeping the same plate proportions.",
  },
  {
    q: "Is a PCOS diet plan for weight loss different from this one?",
    a: "It overlaps heavily, since both target the same insulin resistance, but weight loss specifically may need modest portion awareness layered on top of this pattern rather than a different food list. This plan is the foundation either way.",
  },
  {
    q: "Can diet alone regularize periods in PCOS, or is medication also needed?",
    a: "For many women, consistent low-GI eating alongside movement and sleep meaningfully improves cycle regularity on its own. If cycles, weight, or symptoms haven't shifted after a genuine 3-month effort, that's the signal to involve a doctor rather than a sign the diet failed.",
  },
];

export const Route = createFileRoute("/blog/7-day-pcos-diet-plan-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description:
        "Follow TruWe's 7 day PCOS diet plan with real Indian meals, a full weekly chart, and foods to avoid — built on evidence-based insulin-friendly eating.",
      ogTitle: "7-Day PCOS Diet Plan (India): What to Eat Each Day",
      ogDescription:
        "Real Indian meals, a full weekly chart, and the reasoning behind them — built on insulin-friendly eating.",
      headline: "7-Day PCOS Diet Plan (India): What to Eat Each Day for Hormonal Balance",
      breadcrumbName: "7-Day PCOS Diet Plan",
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      ogImage: `https://truwe.in${heroAsset}`,
      faqs: FAQS,
    }),
});

function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
      {children}
    </p>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
      {children}
    </h2>
  );
}

const TAKEAWAYS = [
  "PCOS weight and symptom struggles are driven mainly by insulin resistance, not a lack of willpower — which is why a generic low-calorie diet often underperforms.",
  "A 7-day PCOS diet plan built on low-GI, protein-forward Indian meals is one of the most consistently evidence-backed approaches for improving cycle regularity and energy.",
  "Building your plate by proportion (half vegetables, a quarter protein, a quarter low-GI grain) is more sustainable than obsessive calorie counting.",
  "Diet works best alongside movement, sleep, and — for some women — targeted supplementation, not as a standalone fix.",
  "Give any honest 7-day PCOS diet plan around 8–12 weeks before expecting visible changes in cycles, skin, or energy.",
];

const PLATE = [
  { share: "Half your plate", what: "Vegetables — palak, lauki, bhindi, cabbage, or a mixed salad" },
  { share: "A quarter", what: "Protein — dal, curd, paneer, eggs, chicken, or fish" },
  { share: "A quarter", what: "A low-GI grain — ragi, jowar, bajra, or a controlled portion of brown rice" },
  { share: "A small finish", what: "Healthy fat — a few nuts, seeds, or a spoon of ghee" },
];

const WEEK: Array<{ day: string; breakfast: string; lunch: string; snack: string; dinner: string }> = [
  { day: "Day 1", breakfast: "Vegetable poha with peanuts", lunch: "Jowar roti + dal + mixed sabzi + salad", snack: "Roasted chana", dinner: "Paneer/grilled chicken with sautéed vegetables" },
  { day: "Day 2", breakfast: "Moong dal chilla with mint chutney", lunch: "Brown rice (half cup) + rajma + cucumber salad", snack: "Buttermilk + roasted seeds", dinner: "Vegetable khichdi" },
  { day: "Day 3", breakfast: "Vegetable oats upma", lunch: "Bajra roti + palak paneer/fish curry + salad", snack: "Handful of almonds", dinner: "Palak soup + multigrain roti" },
  { day: "Day 4", breakfast: "Besan chilla + curd", lunch: "Millet pulao + chana dal + raita", snack: "Sprouts chaat", dinner: "Dal + sautéed seasonal vegetables" },
  { day: "Day 5", breakfast: "Ragi dosa + coconut chutney", lunch: "Roti + chickpea curry/egg curry + salad", snack: "Greek yogurt + flaxseed", dinner: "Grilled tofu/paneer with vegetables" },
  { day: "Day 6", breakfast: "Idli + sambar + mint chutney", lunch: "Quinoa or millet khichdi + curd", snack: "Roasted makhana", dinner: "Vegetable soup + roti" },
  { day: "Day 7", breakfast: "Vegetable upma + curd", lunch: "Brown rice + fish curry/chana masala + salad", snack: "Buttermilk + fruit", dinner: "Light moong dal + steamed vegetables" },
];

const NOTES = [
  "Swap any day for another — none of these are sequence-dependent.",
  "Keep white rice portions small (half a cup) and always paired with dal or vegetables, never alone.",
  "One flexible or treat meal a week is fine — consistency across months matters more than a perfect week.",
  "If you're following this as a PCOS diet plan for weight loss, keep portion sizes moderate rather than cutting entire meals; skipping meals tends to backfire on insulin stability.",
];

const EAT_MORE = [
  "Whole millets — ragi, jowar, bajra",
  "Dals and legumes — moong, chana, rajma, masoor",
  "Non-starchy vegetables — palak, lauki, bhindi, cabbage, capsicum",
  "Curd and buttermilk",
  "Nuts and seeds in moderate portions",
  "Fruit with skin, eaten whole rather than juiced",
];

const LIMIT = [
  "Refined white flour (maida) — biscuits, white bread, most bakery items",
  "Sugary beverages and packaged fruit juices",
  "Deep-fried snacks as a daily habit",
  "Large portions of white rice eaten alone",
  "Highly processed packaged foods with long ingredient lists",
];

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Hormonal Balance
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            7-Day PCOS Diet Plan (India): What to Eat Each Day for Hormonal Balance
          </h1>
          <BlogByline date={PUBLISHED} readingTime="10 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            If you've been searching for a 7-day PCOS diet plan that actually fits an Indian
            kitchen — not almond milk and quinoa bowls you'll abandon by Wednesday — you're in
            the right place. Whether you call it PCOS or PCOD, the food logic is the same, and
            this guide gives you a full week of real, practical meals plus the reasoning behind
            them, so you're not just following a chart blindly.
          </p>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset}
              alt="A week of Indian PCOS-friendly meals laid out on a kitchen table"
              width={1200}
              height={641}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <aside className="mt-8 rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 md:p-6">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              <Sparkles className="h-3.5 w-3.5" />
              Key takeaways
            </p>
            <ul className="mt-3 space-y-2">
              {TAKEAWAYS.map((t) => (
                <li key={t} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                  <span className="min-w-0 break-words">{t}</span>
                </li>
              ))}
            </ul>
          </aside>
        </Reveal>

        <Reveal delay={0.05}>
          <P>
            <strong className="text-brand-deep">Quick answer:</strong> The best 7-day PCOS diet
            plan is a low-glycemic-index, protein-forward Indian eating pattern — millets, dals,
            vegetables, curd, and controlled grain portions — built around a simple plate ratio
            rather than a restrictive food list. It targets insulin resistance, the actual root
            cause behind most PCOS symptoms, and works better as a repeatable weekly pattern than
            a one-time fix.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Why PCOS needs a different approach to diet</H2>
          <P>
            Most generic diet advice assumes your body handles food the same way everyone else's
            does. If you have PCOS, it often doesn't — and that's not a personal failing, it's
            biology.
          </P>
          <P>
            In a large share of PCOS cases, cells stop responding properly to insulin, the hormone
            that moves sugar out of your blood and into your cells for energy. When that signal
            weakens, your body compensates by producing more insulin. That extra insulin does two
            unhelpful things: it pushes your ovaries to produce more androgens (hormones linked to
            irregular cycles, acne, and excess hair), and it makes your body hold onto fat more
            stubbornly, especially around the waist.
          </P>
          <P>
            This is why insulin-sensitising support — through diet, and in some cases through a
            supplement built around 40:1 myo-inositol — works on the actual root cause rather than
            just symptoms. This mechanism is well-documented in clinical research on the
            myo:D-chiro inositol ratio (Nordio M et al., Eur. Rev. Med. Pharmacol. Sci., 2019).
          </P>
          <P>
            Food is one of the most direct levers on this loop, because every meal either spikes
            insulin sharply or keeps it steady. That's the entire logic behind this 7-day PCOS
            diet plan — not eliminating entire food groups, but choosing combinations that don't
            force your body into that extra-insulin response over and over again. If weight is
            your main concern rather than cycles or skin, this same insulin-first logic is exactly
            what we cover in more depth in our guide to{" "}
            <Link
              to="/blog/pcos-metabolic-health-prediabetes"
              className="font-semibold text-brand underline underline-offset-4"
            >
              PCOS and metabolic health
            </Link>
            .
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Build your plate: a simple framework before you follow any chart</H2>
          <P>
            Before jumping into day-by-day meals, it helps to understand the pattern behind them,
            so you can build your own meals on days you deviate from the plan. A reliable way to
            structure each meal, without counting a single calorie:
          </P>
          <figure className="mt-6 overflow-hidden rounded-3xl border border-border">
            <img
              src={plateAsset}
              alt="The PCOS plate method: half vegetables, a quarter protein, a quarter low-GI grain"
              width={1200}
              height={641}
              loading="lazy"
              className="h-auto w-full"
            />
          </figure>
          <div className="mt-6 space-y-4">
            {PLATE.map((s) => (
              <div key={s.share} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-base font-bold text-brand-deep md:text-lg">
                  {s.share}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.what}</p>
              </div>
            ))}
          </div>
          <P>
            This plate method is the backbone of most Indian diet for PCOS recommendations you'll
            see from dietitians, because it's realistic for a home kitchen and doesn't require
            special ingredients. A small serving of nuts, seeds, or ghee also brings in magnesium,
            a mineral tied to insulin signalling — worth paying attention to if you consistently
            fall short of it through food alone, which is where something like{" "}
            <Link
              to="/products/$handle"
              params={{ handle: "mag-absorb-pro" }}
              className="font-semibold text-brand underline underline-offset-4"
            >
              Mag Absorb Pro
            </Link>{" "}
            can fit as a supporting habit. Building meals around fibre-forward vegetables also
            supports the gut-liver axis that helps process hormones and metabolic waste, an area{" "}
            <Link
              to="/products/$handle"
              params={{ handle: "liver-biome-plus" }}
              className="font-semibold text-brand underline underline-offset-4"
            >
              Liver Biome Plus
            </Link>{" "}
            is formulated around, for anyone dealing with sluggish digestion alongside PCOS
            symptoms.
          </P>
          <P>
            Once this ratio becomes second nature, the specific 7-day PCOS diet plan below becomes
            far easier to follow — and far easier to repeat in week two, three, and beyond.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>The 7-day Indian PCOS diet plan</H2>
          <P>
            This is a vegetarian-default PCOS diet chart, with simple non-veg swaps noted for
            lunch or dinner wherever they fit naturally. Portions are indicative — a katori is
            roughly one small bowl. Adjust quantity, not food quality, based on your activity
            level and any guidance from your dietitian.
          </P>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="bg-brand/[0.06] text-left">
                  <th className="px-4 py-3 font-display font-bold text-brand-deep">Day</th>
                  <th className="px-4 py-3 font-display font-bold text-brand-deep">Breakfast</th>
                  <th className="px-4 py-3 font-display font-bold text-brand-deep">Lunch</th>
                  <th className="px-4 py-3 font-display font-bold text-brand-deep">Snack</th>
                  <th className="px-4 py-3 font-display font-bold text-brand-deep">Dinner</th>
                </tr>
              </thead>
              <tbody>
                {WEEK.map((d) => (
                  <tr key={d.day} className="border-t border-border align-top">
                    <td className="whitespace-nowrap px-4 py-3 font-semibold text-brand-deep">{d.day}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.breakfast}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.lunch}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.snack}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.dinner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <figure className="mt-6 overflow-hidden rounded-3xl border border-border">
            <img
              src={weekAsset}
              alt="The full 7-day Indian PCOS diet plan chart, day by day"
              width={1200}
              height={641}
              loading="lazy"
              className="h-auto w-full"
            />
          </figure>
          <P>A few notes to keep this 7-day PCOS diet plan realistic rather than rigid:</P>
          <ul className="mt-4 space-y-2">
            {NOTES.map((n) => (
              <li key={n} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                <span className="min-w-0 break-words">{n}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>What to eat in PCOS — and what to limit</H2>
          <P>
            Knowing what to eat in PCOS matters more than memorising a rigid do-not-eat list, but
            a few categories are genuinely worth limiting.
          </P>
          <h3 className="mt-8 font-display text-lg font-bold text-brand-deep">Foods to eat more of</h3>
          <ul className="mt-4 space-y-2">
            {EAT_MORE.map((s) => (
              <li key={s} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                <span className="min-w-0 break-words">{s}</span>
              </li>
            ))}
          </ul>
          <h3 className="mt-8 font-display text-lg font-bold text-brand-deep">
            PCOS diet foods to avoid or limit
          </h3>
          <ul className="mt-4 space-y-2">
            {LIMIT.map((s) => (
              <li key={s} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                <span className="min-w-0 break-words">{s}</span>
              </li>
            ))}
          </ul>
          <P>
            None of this needs to be absolute. A PCOD diet plan that bans every treat food usually
            fails within a month — the goal is shifting your everyday baseline, not achieving
            perfection.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Where supplements fit alongside this plan</H2>
          <P>
            This 7-day PCOS diet plan is built around the same principle research consistently
            points to for PCOS: improving insulin sensitivity through food choices, meal timing,
            and consistent habits. Some women choose to pair this kind of eating pattern with a
            targeted supplement — most commonly myo-inositol combined with D-chiro-inositol in the
            40:1 ratio studied in PCOS trials — since diet and inositol work on the same
            insulin-sensitivity pathway rather than separate ones.
          </P>
          <P>
            It's worth being clear-eyed here: a supplement is not a substitute for the food choices
            above, and any supplement decision is worth a quick conversation with your doctor,
            particularly if you're managing PCOS alongside another condition or medication. If
            you're comparing options, look for a women's wellbeing supplement that discloses its
            exact inositol ratio and dose rather than hiding behind a proprietary blend label. If
            you're curious about the mechanism and dosing behind the 40:1 ratio, our dedicated
            guide on{" "}
            <Link
              to="/blog/how-to-take-inositol-for-pcos-india"
              className="font-semibold text-brand underline underline-offset-4"
            >
              inositol for PCOS
            </Link>{" "}
            breaks it down in detail.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Beyond diet: two more levers worth knowing</H2>
          <P>
            Diet alone rarely does all the work in PCOS, and being honest about that upfront saves
            frustration later.
          </P>
          <P>
            <strong className="text-brand-deep">Movement:</strong> Around 150–300 minutes of
            moderate activity a week, plus some strength training, helps your muscles use up blood
            sugar more efficiently — which directly supports the same insulin-sensitivity goal as
            this diet plan.
          </P>
          <P>
            <strong className="text-brand-deep">Sleep:</strong> Even a few nights of poor sleep
            measurably reduces insulin sensitivity. A consistent sleep schedule is a free,
            often-overlooked lever alongside any Indian diet for PCOS.
          </P>
          <P>
            Neither replaces the meal plan above — they simply make it work harder for you.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>The bottom line</H2>
          <P>
            A good 7-day PCOS diet plan isn't about restriction — it's about building a repeatable
            weekly pattern that keeps insulin steady, uses real Indian foods, and doesn't require
            you to overhaul your kitchen. Pair it with consistent movement and sleep, and give it
            a genuine 8–12 weeks before judging results. Small, sustainable changes, followed
            consistently, tend to outperform any short-term perfect week.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <figure className="mt-12 overflow-hidden rounded-3xl border border-border">
            <img
              src={faqAsset}
              alt="Common questions about the 7-day PCOS diet plan"
              width={1200}
              height={641}
              loading="lazy"
              className="h-auto w-full"
            />
          </figure>
          <section className="mt-10">
            <h2 className="font-display text-2xl font-extrabold text-brand-deep md:text-3xl">
              Frequently asked questions
            </h2>
            <div className="mt-6 space-y-3">
              {FAQS.map((f) => (
                <div key={f.q} className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="font-display text-base font-bold text-brand-deep md:text-lg">
                    {f.q}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="hormonal" />
          <ClusterLinks cluster="hormonal" />
          <AuthorityFlow cluster="hormonal" />
          <RelatedReading articles={relatedArticles(SLUG)} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. This article does not diagnose or treat PCOS. Speak with a qualified
            clinician or dietitian before making major dietary changes or starting a supplement,
            especially if you are pregnant, breastfeeding, or taking prescription medication.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
